<template lang="pug">
#default-family-permitted-function-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 家族の使用機能の登録時設定

    .box-body.register-body
      .note.mb-2
        span ご家族のユーザー登録時の使用機能設定を選択して更新してください。
        br
        span ユーザー登録後の家族の使用機能（個別設定）は、家族の使用機能（個別設定画面）から変更することができます。
      .function-list.d-flex.flex-column.align-items-center
        .content.d-flex.flex-row.align-items-center(v-for="item in permittedFunctionItems")
          .icon.d-flex.flex-column.align-items-center
            img(:src="item.iconUrl")
            span.used(v-if="item.permitted == true") 使用可
            span.unused(v-else) 使用不可
          .function.flex-grow-1
            .title {{ item.title }}
            .text {{ item.text }}
          .buttons.d-flex.flex-row-reverse
            UsableFunctionSwitch(v-model="item.updatedPermitted")

    .box-footer.register-footer
      .buttons
        button.btn.update-btn(@click="onUpdate" type="button") 更新
        button.btn.cancel-btn(@click="gotoFamilyPermittedFunctionList" type="button") キャンセル

  // modals
  OkCancelModal(v-model="showUpdateModal" title="家族個別設定の家族登録時設定を更新しますか？" @ok="updateDefaultFamilyPermittedFunction(facility)")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoFamilyPermittedFunctionList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import FacilitiesService, { FacilityPatchModel } from "@/commons/services/Facilities";
import { FacilityGetModel } from "@/commons/services/Facilities";
import { FamilyPermittedFunctions } from "@/commons/services/Families";
import SystemError from "@/commons/models/SystemError";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import UsableFunctionSwitch from "@/pc/basics/UsableFunctionSwitch.vue";
import { PcUrl } from "@/commons/constants/PcUrl";

/**
 * ### 家族の使用機能可否 デフォルト設定タブコンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    UsableFunctionSwitch
  }
})
export default class DefaultFamilyPermittedFunctionEdit extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "読込み中...";

  /** 家族の機能使用可否更新モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 家族の機能使用可否更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** 施設 */
  facility: FacilityGetModel | null = null;
  /** デフォルト設定 */
  permittedFunctionItems: Array<PermittedFunctionListItem> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const facility: FacilityGetModel = await this.getFacility();
    this.facility = facility;
    this.permittedFunctionItems = this.createPermittedFunctionItems(facility.defaultFamilyPermittedFunctions);

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設取得
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### 機能の使用可否から目次の情報を作成する
   *
   * - 画像のURL等、保持していない情報が多いのでfor文等は使用できない
   */
  public createPermittedFunctionItems(permittedFunctions: FamilyPermittedFunctions): Array<PermittedFunctionListItem> {
    let items: Array<PermittedFunctionListItem> = [
      {
        id: "opinion",
        permitted: permittedFunctions.opinion,
        iconUrl: require("../../../assets/permitted-function-opinion-icon.png"),
        title: "家族からの連絡",
        text: "家族から施設に連絡・問い合わせが出来ます。施設では内容の確認と返信が出来ます。",
        linkUrl: "#opinion",
        updatedPermitted: permittedFunctions.opinion
      },
      {
        id: "picture",
        permitted: permittedFunctions.picture,
        iconUrl: require("../../../assets/permitted-function-picture-icon.png"),
        title: "写真印刷依頼（家族からの写真送付）",
        text: "家族が施設利用者（入居者）に見せたい写真をメッセージ付きで送ることが出来ます。",
        linkUrl: "#picture",
        updatedPermitted: permittedFunctions.picture
      }
    ];

    return items;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public onUpdate(): void {
    this.showUpdateModal = true;
    return;
  }

  /**
   * ### 登録時設定を更新する
   */
  public async updateDefaultFamilyPermittedFunction(facility: FacilityGetModel): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: FacilityPatchModel = this.createRequestBody(facility);
      await this.facilitiesService.patch(userStore, requestBody);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;

      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### リクエストボディを作成する
   */
  public createRequestBody(facility: FacilityGetModel): FacilityPatchModel {
    const createRequestBody: FacilityPatchModel = {
      name: facility.name,
      tel: facility.tel,
      url: facility.url,
      permittedFunctions: facility.permittedFunctions,
      defaultFamilyPermittedFunctions: {
        opinion: this.permittedFunctionItems[0].updatedPermitted,
        picture: this.permittedFunctionItems[1].updatedPermitted
      },
      useAdmissionApplicationTemplateId: facility.useAdmissionApplicationTemplateId,
      useAdmissionJudgementTemplateId: facility.useAdmissionJudgementTemplateId,
      recruitmentRoom: facility.recruitmentRoom,
      updated: facility.updated
    };

    return createRequestBody;
  }

  /**
   * ### 家族個別設定の確認・変更画面へ遷移する
   */
  public gotoFamilyPermittedFunctionList(): void {
    this.$router.push(PcUrl.FamilyPermittedFunctionList);
    return;
  }
}

interface PermittedFunctionListItem {
  /** ページ内リンク用のID */
  id: string;
  /** 機能の使用可否 */
  permitted: boolean;
  /** アイコン画像URL */
  iconUrl: string;
  /** 機能名 */
  title: string;
  /** 機能説明文 */
  text: string;
  /** ページ内リンクURL */
  linkUrl: string;
  /** 変更後機能の使用可否 */
  updatedPermitted: boolean;
}

export enum PermittedFunctionType {
  UNKNOWN = -1,
  OPINION = 1,
  PICTURE = 2
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#default-family-permitted-function-edit {
  max-width: 920px;
  margin-left: auto;
  margin-right: auto;

  .note {
    padding: 0 0 10px 10px;
  }

  .function-list {
    width: 100%;

    .content {
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      // アイコン
      .icon {
        min-width: 80px;
        width: 80px;
        margin-right: 12px;
        // 使用中・未使用
        .used,
        .unused {
          font-weight: bold;
        }
        .used {
          color: $font-key-color;
        }
        .unused {
          color: $font-disable-font-color;
        }
      }
      // 機能名
      .function {
        margin-right: 12px;
        .title {
          font-size: 18px;
          font-weight: bold;
        }
      }
      // ON/OFFスイッチ
      .buttons {
        width: 130px;
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
